import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FaArrowLeft,
    FaMapMarkerAlt,
    FaRegClock,
    FaTicketAlt,
    FaExternalLinkAlt,
    FaTimes
} from "react-icons/fa";
import { getDetail, getTopFrames } from "../../services/Booths";

export default function BoothDetail() {
    const { boothID } = useParams();
    const navigate = useNavigate();
    const [booth, setBooth] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const [trendData, setTrendData] = useState({ 
        topFiveTrend: [], 
        frameTrend: [], 
        categories: [] 
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [selectedFrame, setSelectedFrame] = useState(null);

    const fetchedBoothIdRef = useRef(null);

    useEffect(() => {
        if (boothID && fetchedBoothIdRef.current !== boothID) {
            fetchedBoothIdRef.current = boothID; 

            getDetail(boothID)
                .then((response) => {
                    if (response && response.success == 1) {
                        setBooth(response.data);
                    }
                })
                .catch((err) => console.error("Error fetching detail:", err))
                .finally(() => setLoading(false));

            getTopFrames(boothID)
                .then((response) => {
                    if (response && response.success == 1) {
                        setTrendData({
                            topFiveTrend: response.topFiveTrend,
                            frameTrend: response.frameTrend,
                            categories: response.categories || []
                        });
                    }
                })
                .catch((err) => console.error("Error fetching top frames:", err));
        }
    }, [boothID]);

    // Helper format jam
    const formatTime = (timeStr) => {
        if (!timeStr) return "";
        return timeStr.slice(0, 5).replace(":", ".");
    };

    // Helper ekstraksi array tag
    const getTagsArray = (tags) => {
        if (!tags) return [];
        if (Array.isArray(tags)) {
            return tags.map((t) => (typeof t === "object" ? t.name : t)).filter(Boolean);
        }
        if (typeof tags === "string") {
            try {
                const parsed = JSON.parse(tags);
                if (Array.isArray(parsed)) {
                    return parsed.map((t) => (typeof t === "object" ? t.name : t)).filter(Boolean);
                }
            } catch (e) { }
            return tags.split(",").map((t) => t.trim()).filter(Boolean);
        }
        return [];
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50 flex items-center justify-center font-sans">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-[#2b478e] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium text-sm tracking-wide">Memuat detail booth...</p>
                </div>
            </div>
        );
    }

    if (!booth) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50 flex flex-col items-center justify-center p-6 font-sans">
                <div className="bg-white/80 backdrop-blur-md p-6 rounded-3xl shadow-lg text-center max-w-xs w-full border border-white">
                    <p className="text-gray-700 font-semibold mb-4">Booth tidak ditemukan.</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full py-2.5 bg-[#2b478e] hover:bg-[#e7ac46] active:scale-95 text-white rounded-full font-bold text-sm transition-all shadow-md"
                    >
                        Kembali
                    </button>
                </div>
            </div>
        );
    }

    const tagsList = getTagsArray(booth.merchant_tags);

    // List kategori dengan tambahan default 'All Frame' (id: 0) di awal
    const allCategories = [{ id: 0, name: "All Frame" }, ...trendData.categories];
    
    // Filter frame secara lokal (client-side) tanpa fetch ulang
    const filteredFrames = selectedCategory === 0 
        ? trendData.frameTrend 
        : trendData.frameTrend.filter(frame => frame.category_id === selectedCategory);

    return (
        <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50 flex justify-center py-6 px-4 font-sans text-gray-800 relative">
            <div className="w-full max-w-2xl flex flex-col gap-4">

                {/* Header Navigasi */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 bg-white/80 backdrop-blur-md px-4 py-2 rounded-full text-gray-800 font-semibold text-xs shadow-sm hover:bg-white active:scale-95 transition-all border border-gray-100"
                    >
                        <FaArrowLeft className="text-xs text-[#2b478e]" />
                        <span>Kembali</span>
                    </button>

                    <div className="w-10 h-10 rounded-full overflow-hidden shadow-md bg-white border-2 border-white flex-shrink-0">
                        <img
                            src="/latest-logo.jpg"
                            alt="Sukhakala Logo"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>

                {/* Card Main Wrapper */}
                <div className="bg-white/80 backdrop-blur-md rounded-3xl p-5 shadow-xl border border-white/60 flex flex-col gap-4">

                    {/* Hero Banner Image */}
                    <div className="relative w-full rounded-2xl overflow-hidden shadow-sm bg-gray-100 group">
                        <img
                            src={booth.image_url || "/latest-logo.jpg"}
                            alt={booth.maps_name || booth.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                    </div>

                    {/* Title & Location Tag */}
                    <div>
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
                            <span>📍 {booth.cityName}</span>
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 leading-tight">
                            {booth.maps_name || booth.name}
                        </h1>
                    </div>

                    {/* Tags List */}
                    {tagsList.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            {tagsList.map((tag, idx) => (
                                <span
                                    key={idx}
                                    className="bg-[#2b478e]/10 text-[#2b478e] px-3 py-1 rounded-full text-[11px] font-mono font-bold tracking-tight border border-[#2b478e]/20"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}

                    <hr className="border-gray-100/80 my-1" />

                    {/* Detail Info Cards */}
                    <div className="flex flex-col gap-2.5 text-sm">
                        {booth.address && (
                            <div className="flex items-start gap-3 bg-slate-50/80 p-3 rounded-2xl border border-slate-100/80">
                                <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#2b478e] flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <FaMapMarkerAlt className="text-sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Alamat</span>
                                    <p className="text-gray-700 leading-snug font-medium text-xs sm:text-sm">{booth.address}</p>
                                </div>
                            </div>
                        )}

                        {(booth.open_hour || booth.close_hour) && (
                            <div className="flex items-center gap-3 bg-slate-50/80 p-3 rounded-2xl border border-slate-100/80">
                                <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#2b478e] flex items-center justify-center flex-shrink-0">
                                    <FaRegClock className="text-sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Jam Operasional</span>
                                    <p className="text-gray-800 font-semibold text-xs sm:text-sm font-mono">
                                        {formatTime(booth.open_hour)} - {formatTime(booth.close_hour)} WIB
                                    </p>
                                </div>
                            </div>
                        )}

                        {booth.price !== undefined && booth.price !== null && (
                            <div className="flex items-center gap-3 bg-slate-50/80 p-3 rounded-2xl border border-slate-100/80">
                                <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#2b478e] flex items-center justify-center flex-shrink-0">
                                    <FaTicketAlt className="text-sm rotate-90" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Harga Sesi</span>
                                    <p className="text-gray-800 font-bold text-xs sm:text-sm font-mono">
                                        Rp {Number(booth.price).toLocaleString("id-ID")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Button Buka Peta */}
                    {booth.maps_url && (
                        <a
                            href={booth.maps_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 w-full bg-[#2b478e] hover:bg-[#e7ac46] active:scale-[0.98] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 shadow-lg shadow-blue-900/15 border border-black/10 text-sm"
                        >
                            <FaMapMarkerAlt className="text-base" />
                            <span>Buka Petunjuk Peta</span>
                            <FaExternalLinkAlt className="text-xs opacity-75 ml-0.5" />
                        </a>
                    )}

                    {/* ===== SECTION: TOP 5 FRAME BULAN INI ===== */}
                    {trendData.topFiveTrend.length > 0 && (
                        <div className="mt-4 flex flex-col gap-3">
                            <div className="flex justify-between items-center px-1">
                                <span className="text-[11px] font-bold text-gray-800 uppercase tracking-wider">
                                    TOP 5 FRAME BULAN INI
                                </span>
                                <button
                                    onClick={() => {
                                        setIsModalOpen(true);
                                        setSelectedCategory(0); // Reset ke All saat buka modal
                                    }}
                                    className="text-[11px] font-bold text-gray-800 hover:text-[#e7ac46] transition-colors cursor-pointer"
                                >
                                    Lihat semua ({trendData.frameTrend.length})
                                </button>
                            </div>

                            {/* Horizontal Scroll Area */}
                            <div
                                className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory"
                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                                {trendData.topFiveTrend.map((frame, idx) => (
                                    <div
                                        key={`top-${idx}`}
                                        onClick={() => setSelectedFrame(frame)}
                                        className="flex-shrink-0 w-28 aspect-[2/3] overflow-hidden shadow-sm border border-gray-200 snap-center bg-gray-100 cursor-pointer hover:opacity-90 active:scale-95 transition-all"
                                    >
                                        <img
                                            src={frame.thumbnail_url}
                                            alt={frame.frame_name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-full flex justify-center items-center py-4 mt-2 mb-6">
                    <p className="text-xs text-gray-400 font-medium tracking-wide">
                        Powered by <span className="font-bold text-[#2b478e]">Sukhakala</span>
                    </p>
                </div>
            </div>

            {/* ===== MODAL: SEMUA FRAME TREND ===== */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6 transition-opacity">
                    <div className="bg-white w-full rounded-t-2xl sm:rounded-2xl max-w-lg h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden shadow-2xl relative">

                        {/* Header Modal */}
                        <div className="flex justify-between items-center p-4 px-5 border-b border-gray-100 bg-white z-10 shadow-sm flex-shrink-0">
                            <h2 className="text-xs sm:text-sm font-bold text-gray-800 tracking-tight font-mono truncate mr-2">
                                Top 5 frame bulan ini • {booth.maps_name || booth.name}
                            </h2>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0 cursor-pointer rounded-full"
                            >
                                <FaTimes className="text-lg" />
                            </button>
                        </div>

                        {/* Kategori Filter Scrollable (Hard Shadow Style) */}
                        <div
                            className="flex gap-3 overflow-x-auto px-4 sm:px-5 pt-4 pb-3 bg-slate-50/80 flex-shrink-0 border-b border-gray-200 w-[90%] mx-auto"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {allCategories.map((cat) => {
                                const isActive = selectedCategory === cat.id;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className={`flex-shrink-0 px-5 py-1.5 rounded-full border-[1.5px] border-black font-semibold text-[13px] tracking-wide whitespace-nowrap transition-all shadow-[2px_3px_0px_rgba(0,0,0,1)] active:shadow-[0px_0px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[3px] ${isActive
                                                ? 'bg-[#3eb674] text-black'  // Warna hijau untuk frame aktif
                                                : 'bg-[#76c3fa] text-black'  // Warna biru untuk frame tidak aktif
                                            }`}
                                    >
                                        {cat.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Body Modal Grid Frame */}
                        <div className="p-4 sm:p-5 overflow-y-auto grid grid-cols-3 gap-y-3 bg-slate-50/80 flex-1 content-start">
                            {filteredFrames.length > 0 ? (
                                filteredFrames.map((frame, idx) => (
                                    <div
                                        key={`all-${idx}`}
                                        onClick={() => setSelectedFrame(frame)}
                                        className="group relative flex flex-col bg-white p-1 shadow-sm border border-gray-200/80 hover:shadow-md transition-all cursor-pointer active:scale-95"
                                    >
                                        <div className="w-full aspect-[2/3] overflow-hidden bg-gray-100 flex items-center justify-center">
                                            <img
                                                src={frame.thumbnail_url}
                                                alt={frame.frame_name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-3 py-10 flex justify-center text-gray-400 text-sm font-medium">
                                    Frame untuk kategori ini belum tersedia.
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {/* ===== MODAL PREVIEW / SHOW FULL FRAME ===== */}
            {selectedFrame && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 transition-all"
                    onClick={() => setSelectedFrame(null)}
                >
                    <div
                        className="relative bg-white/10 p-3.5 rounded-xl max-w-sm w-full flex flex-col items-center gap-3 backdrop-blur-xl border border-white/20 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Tombol Close */}
                        <button
                            onClick={() => setSelectedFrame(null)}
                            className="absolute -top-3 -right-3 w-9 h-9 bg-white text-gray-800 rounded-full flex items-center justify-center shadow-xl hover:bg-red-50 hover:text-red-600 transition-all cursor-pointer z-10 border border-gray-100"
                        >
                            <FaTimes className="text-sm" />
                        </button>

                        {/* Frame Full Preview */}
                        <div className="w-full max-h-[65vh] overflow-hidden flex items-center justify-center">
                            <img
                                src={selectedFrame.thumbnail_url}
                                alt={selectedFrame.frame_name}
                                className="w-full h-full object-contain max-h-[65vh]"
                            />
                        </div>

                        {/* Info Nama Frame & Tag Spesifikasi */}
                        <div className="w-full text-center text-white px-2 pb-1">
                            {(selectedFrame.printer_type || selectedFrame.paper_type) && (
                                <div className="flex justify-center items-center gap-2 mt-1.5 text-[11px] text-gray-200 font-mono">
                                    {selectedFrame.printer_type && (
                                        <span className="bg-white/20 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-white/20">
                                            🖨️ {selectedFrame.printer_type}
                                        </span>
                                    )}
                                    {selectedFrame.paper_type && (
                                        <span className="bg-white/20 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-white/20">
                                            📄 {selectedFrame.paper_type}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}